import React, { useState, memo, useCallback, useEffect } from 'react';
import { IconClipboardList, IconTrash } from '@tabler/icons-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { CreatedRoom } from '../../../../services/create-booking-v4/hooks/useCreateGroupBookingV4';
import useIndexBookingExtra from '../../../../services/booking-extra/hooks/useIndexBookingExtra';
import ExtraDescription from '../../../../shared/components/ExtraDescription';
import { isPosExtra, isCancelledExtra, POS_LOCK_MESSAGE } from '../../../../shared/utils/posExtra';
import { useCreateBookingExtra } from '../../../../services/booking-extra/hooks/useCreateBookingExtra';
import { useDeleteBookingExtra } from '../../../../services/booking-extra/hooks/useDeleteBookingExtra';
import CurrencyInputRef from '../../../../shared/components/form/CurrencyInputRef';
import DateInput from '../../../../shared/components/form/DateInput';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import Loader from '../../../../shared/components/Loader';
import SelectInventoryModal from '../../../../shared/components/SelectInventoryModal';
import { useAuth } from '../../../../auth/context/AuthProvider';

// --- Tipe Props ---

type ExtraSectionPropsV4 = {
    createdRooms: CreatedRoom[];
    onTotalExtrasChange?: (total: number) => void;
};

type SingleRoomExtraManagerProps = {
    roomIndex: number;
    bookingRoomId: string;
    onRoomTotalChange?: (bookingRoomId: string, total: number) => void;
};

// --- Sub-komponen untuk Mengelola Extra per Kamar ---

const SingleRoomExtraManager: React.FC<SingleRoomExtraManagerProps> = ({ roomIndex, bookingRoomId, onRoomTotalChange }) => {
    const queryClient = useQueryClient();

    // Fetch extras for this room
    const { data: extrasData, isFetching: isExtrasFetching } = useIndexBookingExtra({
        filters: { booking_room_id: bookingRoomId },
    });

    // Mutations
    const { mutate: createExtra, isPending: isCreating } = useCreateBookingExtra();
    const { mutate: deleteExtra, isPending: isDeleting } = useDeleteBookingExtra();

    // Modal state for inventory selection
    const [showInventoryModal, setShowInventoryModal] = useState(false);

    // Local form state
    const getInitialDate = () => new Date().toISOString().split('T')[0];
    const [date, setDate] = useState<string>(getInitialDate());
    const [itemName, setItemName] = useState<string>('');
    const [qty, setQty] = useState<number>(1);
    const [amount, setAmount] = useState<number>(0);

    // Handle inventory selection from modal
    const handleInventorySelect = (inventory: { id: string; item_name: string; price: string }) => {
        setItemName(inventory.item_name);
        setAmount(Number(inventory.price) || 0);
    };

    // Reset form
    const resetForm = () => {
        setDate(getInitialDate());
        setItemName('');
        setQty(1);
        setAmount(0);
    };

    const { hasRole, hasPermission } = useAuth();

    // Handle add extra
    const handleAddExtra = useCallback(() => {
        if (!hasRole('dev') && !hasPermission('booking_extra')) {
            toast.error('You do not have permission to add extra');
            return;
        }

        if (!itemName.trim()) {
            toast.error('Please enter an item name.');
            return;
        }
        if (qty < 1) {
            toast.error('Quantity must be at least 1.');
            return;
        }
        if (amount <= 0) {
            toast.error('Amount must be greater than 0.');
            return;
        }

        toast.loading('Adding extra...', { id: 'create-extra' });

        createExtra({
            booking_room_id: bookingRoomId,
            date,
            item_name: itemName,
            qty,
            amount,
        }, {
            onSuccess: async () => {
                await queryClient.invalidateQueries({ queryKey: ['booking-extras'] });
                toast.success('Extra added successfully', { id: 'create-extra' });
                resetForm();
            },
            onError: () => {
                toast.error('Failed to add extra', { id: 'create-extra' });
            },
        });
    }, [bookingRoomId, date, itemName, qty, amount, createExtra, queryClient, hasRole, hasPermission]);

    // Handle delete extra
    const handleDeleteExtra = useCallback((id: string) => {
        if (!hasRole('dev') && !hasPermission('booking_extra')) {
            toast.error('You do not have permission to delete extra');
            return;
        }

        if (isDeleting) return;
        toast.loading('Removing extra...', { id: 'delete-extra' });
        deleteExtra(id, {
            onSuccess: async () => {
                await queryClient.invalidateQueries({ queryKey: ['booking-extras'] });
                toast.success('Extra removed successfully', { id: 'delete-extra' });
            },
            onError: () => {
                toast.error('Failed to remove extra', { id: 'delete-extra' });
            },
        });
    }, [deleteExtra, isDeleting, queryClient, hasRole, hasPermission]);

    // Calculate total extras for this room
    const totalExtras = extrasData?.data?.reduce((sum, extra) => sum + Number(extra.amount) * extra.qty, 0) ?? 0;

    // Report total to parent when it changes
    useEffect(() => {
        onRoomTotalChange?.(bookingRoomId, totalExtras);
    }, [totalExtras, bookingRoomId, onRoomTotalChange]);

    const isPending = isCreating || isDeleting;

    return (
        <div className="mb-5 p-4 border rounded-3 bg-light">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fw-bold text-primary">Extras for Room {roomIndex + 1}</h5>
                <div className="text-end">
                    <small className="text-muted d-block">Total Extras</small>
                    <span className="fw-bold fs-5">{formatPrice(totalExtras)}</span>
                </div>
            </div>

            {/* Form Penambahan Extra */}
            <div className="row gy-3 align-items-end mb-4">
                <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label">Date</label>
                    <DateInput
                        className="form-control"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        disabled={isPending}
                    />
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label">Template</label>
                    <button
                        type="button"
                        className="btn btn-outline-secondary w-100"
                        onClick={() => setShowInventoryModal(true)}
                        disabled={isPending}
                    >
                        <IconClipboardList size={16} className="me-1" />
                        Select Template
                    </button>
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label">Item Name</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Minibar, Laundry"
                        value={itemName}
                        onChange={(e) => setItemName(e.target.value)}
                        disabled={isPending}
                    />
                </div>
                <div className="col-12 col-md-3 col-lg-1">
                    <label className="form-label">Qty</label>
                    <input
                        type="number"
                        className="form-control"
                        min={1}
                        value={qty}
                        onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                        disabled={isPending}
                    />
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label">Amount</label>
                    <CurrencyInputRef
                        name={`extra_amount_${roomIndex}`}
                        value={amount}
                        onChange={(_e, rawValue) => setAmount(Number(rawValue))}
                    />
                </div>
                <div className="col-12 col-lg-2">
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleAddExtra}
                        disabled={isPending}
                    >
                        {isCreating ? 'Adding...' : 'Add Extra'}
                    </button>
                </div>
            </div>

            {/* Tabel Daftar Extras */}
            {isExtrasFetching ? (
                <Loader />
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-striped mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Date</th>
                                <th>Item Name</th>
                                <th className='text-center'>Qty</th>
                                <th className='text-end'>Amount</th>
                                <th className='text-end'>Subtotal</th>
                                <th style={{ width: '80px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!extrasData?.data || extrasData.data.length === 0) ? (
                                <tr><td colSpan={6} className="text-center text-muted">No extras added for this room.</td></tr>
                            ) : (
                                extrasData.data.map((extra) => {
                                    const fromPos = isPosExtra(extra.source);

                                    return (
                                        <tr key={extra.id}>
                                            <td>{new Date(extra.date).toLocaleDateString()}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className={isCancelledExtra(extra.item_name) ? 'text-decoration-line-through text-muted' : undefined}>
                                                        {extra.item_name}
                                                    </span>
                                                    {fromPos && <span className="badge bg-info-subtle text-info-emphasis">POS</span>}
                                                </div>
                                                <ExtraDescription description={extra.description} className="mt-1" />
                                            </td>
                                            <td className='text-center'>{extra.qty}</td>
                                            <td className='text-end'>{formatPrice(Number(extra.amount))}</td>
                                            <td className='text-end'>{formatPrice(Number(extra.amount) * extra.qty)}</td>
                                            <td>
                                                {fromPos ? (
                                                    // Terkunci di API — pembatalan hanya lewat POS.
                                                    <span className="text-muted small" title={POS_LOCK_MESSAGE}>Locked</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDeleteExtra(extra.id)}
                                                        disabled={isDeleting}
                                                    >
                                                        <IconTrash size={16} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="table-light fw-bold">
                                <td colSpan={4} className="text-end">Total Extras for this Room:</td>
                                <td colSpan={2} className="text-end fs-5">{formatPrice(totalExtras)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* Inventory Selection Modal */}
            <SelectInventoryModal
                show={showInventoryModal}
                onClose={() => setShowInventoryModal(false)}
                onSelect={handleInventorySelect}
            />
        </div>
    );
};


// --- Komponen Utama ---

const ExtraSectionV4: React.FC<ExtraSectionPropsV4> = ({ createdRooms, onTotalExtrasChange }) => {
    const [roomTotals, setRoomTotals] = useState<Record<string, number>>({});

    // Callback for each room to report its total
    const handleRoomTotalChange = useCallback((bookingRoomId: string, total: number) => {
        setRoomTotals(prev => ({ ...prev, [bookingRoomId]: total }));
    }, []);

    // Calculate and report total extras whenever roomTotals changes
    useEffect(() => {
        const total = Object.values(roomTotals).reduce((sum, t) => sum + t, 0);
        onTotalExtrasChange?.(total);
    }, [roomTotals, onTotalExtrasChange]);

    if (!createdRooms || createdRooms.length === 0) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-info">
                    No rooms available. Please save the booking first before adding extras.
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            {createdRooms.map((room) => (
                <SingleRoomExtraManager
                    key={room.bookingId}
                    roomIndex={room.index}
                    bookingRoomId={room.bookingId}
                    onRoomTotalChange={handleRoomTotalChange}
                />
            ))}
        </div>
    );
};

export default memo(ExtraSectionV4);
